/**
 * Compose a one-click "create orchestrator agent for workflow X" flow.
 *
 * Three sequential steps, each calling an existing endpoint -- this module
 * owns the orchestration only:
 *
 *   1. If the workflow is not yet ``publish_to_api: true``, fetch its full
 *      payload via the workflow service and re-persist with the flag set.
 *      This is what makes the workflow callable from the agent's
 *      ``workflow_allowlist`` (the agentic-backend gateway gates A2A
 *      skill exposure on this column).
 *   2. Best-effort: ask platform-api to auto-generate a description for
 *      the new agent (LLM call).  On any failure we fall back to a
 *      static "A2A orchestrator for the {workflow.name} workflow"
 *      string -- the orchestrator must still get created.
 *   3. POST ``/agents/{orgId}`` with orchestrator-flavoured defaults:
 *      ``temperature: 0.3``, ``enable_workflow_authoring: false``,
 *      ``workflow_allowlist: [workflow.id]``, ``exposed_via_a2a: true``.
 *      Legacy bearer delivery is suppressed. App builders mint a labeled,
 *      scoped deployment handoff only when they deploy a server-side BFF.
 *
 * Returns the ``AgentCreateResponse`` with discovery URLs and an empty legacy
 * bearer field. Throws on any unrecoverable error from steps 1 or 3.
 */

import {
  autoGenerateAgentDescription,
  createAgent,
} from '@/services/agents/config';
import { getWorkflow } from '@/services/workflow/get-workflow';
import { updateWorkflow } from '@/services/workflow/update-workflow';
import type {
  AgentCreateRequest,
  AgentCreateResponse,
} from '@/types/agent-types';
import type { CustomEdge } from '@/types/edge-types';
import type { CustomNode } from '@/types/node-types';
import type { Workflow, WorkflowDetails } from '@/types/workflow-types';

export type OrchestratorProgress =
  | 'publishing_workflow'
  | 'generating_description'
  | 'creating_agent'
  | 'done';

export interface CreateOrchestratorOptions {
  onProgress?: (step: OrchestratorProgress) => void;
}

const ORCHESTRATOR_SYSTEM_PROMPT = `You are an A2A orchestrator agent. Interpret the caller's intent and invoke the workflow skill exposed on your agent card. Prefer the bound workflow for this agent. Be concise and operational; do not invoke workflows outside your allowlist.
When you present the user with the files or inputs necessary for running a workflow, you should also present them with the option of modifying the TASK prompts of agents in a workflow. Tell them the agents they can modify the task prompts of. Do not print the task prompts (unless they ask), just tell them what agent prompts in a workflow are available for modification ( eg "you can modify the Capability Mapper Agent prompt"). If the user wants to modify the prompt of an agent they will tell you. When they tell you they want to modify a prompt, you should tell them the prompt and the elements in it that it makes sense they could change for a particular agent. when they make the change, you should make the change to the task but not save the change only use it runs of the workflow during the user session. If any of the nodes are marked as primary outputs in their description, then only return output files they generate, do not return the outputs of other nodes, except for evals node outputs and the eval rollup - Else, remain on default behavior.`;

// Cap on the agent ``name`` field per platform-api schema.  The
// orchestrator suffix is short (" Orchestrator" = 13 chars), but
// long workflow names still need truncating.
const _NAME_MAX_LENGTH = 100;
const _ORCH_SUFFIX = ' Orchestrator';

function _orchestratorName(workflowName: string): string {
  const trimmed = (workflowName || 'Untitled workflow').trim();
  const candidate = `${trimmed}${_ORCH_SUFFIX}`;
  if (candidate.length <= _NAME_MAX_LENGTH) return candidate;
  const room = _NAME_MAX_LENGTH - _ORCH_SUFFIX.length - 1; // -1 for ellipsis
  return `${trimmed.slice(0, room)}…${_ORCH_SUFFIX}`;
}

function _fallbackDescription(workflowName: string): string {
  return `A2A orchestrator for the ${workflowName} workflow`;
}

/**
 * Re-persist the workflow with ``publish_to_api: true`` if it isn't
 * already.  The agentic-backend's ``POST /api/v1/orgs/{org}/workflows``
 * upsert is the only path that updates this column (the legacy PATCH
 * is name/description/icon/color only).  Skips the round-trip when
 * the flag is already set.
 */
async function _ensurePublishToApi(
  workflow: WorkflowDetails,
  orgId: string,
): Promise<void> {
  if (workflow.publish_to_api === true) return;

  const detailResponse = await getWorkflow(workflow.workflow_id, orgId);
  const inner = detailResponse?.data?.workflow;
  const payload = inner?.payload;
  if (!payload) {
    throw new Error(
      detailResponse?.detail ||
        `Could not load workflow ${workflow.workflow_id} payload to enable publish_to_api`,
    );
  }

  // updateWorkflow expects a full Workflow shape (it serialises nodes/edges
  // back through the workflow-builder upsert).  Build a minimal Workflow
  // from the AdvancedWorkflowPayload we just fetched, then flip the flag.
  const fullWorkflow: Workflow = {
    id: payload.id,
    session_id: payload.session_id,
    name: payload.name,
    nodes: (payload.nodes as CustomNode[]) ?? [],
    edges: (payload.edges as CustomEdge[]) ?? [],
    description: payload.description ?? '',
    publish_to_template: payload.publish_to_template ?? false,
    publish_to_api: true,
    screenshot: payload.screenshot,
    metadata: payload.metadata as Workflow['metadata'],
  };

  const update = await updateWorkflow(fullWorkflow, orgId);
  if (update?.detail) {
    throw new Error(update.detail);
  }
}

/**
 * Build the create-agent payload for an orchestrator bound to a single
 * workflow.  Exposed (rather than inlined) so the modal can preview it
 * and tests can pin the field-mapping contract.
 */
export function buildOrchestratorAgentRequest(
  workflow: WorkflowDetails,
  description: string,
): AgentCreateRequest {
  return {
    name: _orchestratorName(workflow.name),
    description,
    icon: workflow.icon ?? '',
    color: workflow.color ?? '',
    model_provider: 'anthropic',
    model_name: 'claude-opus-5',
    system_prompt: ORCHESTRATOR_SYSTEM_PROMPT,
    max_tokens: 32000,
    temperature: 0.3,
    top_p: 1.0,
    thinking_enabled: false,
    thinking_budget_tokens: 8192,
    enable_workflow_runtime: true,
    enable_workflow_authoring: false,
    enable_a2a_outbound: true,
    // An orchestrator built from a workflow is the case scheduling exists
    // for: "run this one every Monday". Stated rather than left to the
    // server default so the capability set here reads in full.
    enable_scheduling: true,
    workflow_allowlist: [workflow.workflow_id],
    workflow_allowlist_labels: [],
    allow_user_workflows: false,
    mcp_tool_allowlist: [],
    data_collection_allowlist: [],
    guardrail_allowlist: [],
    peer_agent_allowlist: [],
    custom_skills: [],
    skill_overrides: {},
    exposed_via_a2a: true,
    suppress_bearer_delivery: true,
  };
}

/**
 * Create an A2A orchestrator agent bound to a single workflow.
 *
 * Steps (each surfaced via ``onProgress`` for the modal's loading copy):
 *
 *   1. Ensure ``publish_to_api: true`` on the workflow (no-op if already
 *      set).  Throws if we can't load the full payload to update.
 *   2. Best-effort auto-generate a description.  Failures fall back to
 *      a static string -- never blocks creation.
 *   3. POST the create payload.  Throws if the create fails.
 *
 * Returns the full ``AgentCreateResponse`` with discovery URLs. The legacy
 * ``a2a_bearer_token`` field is intentionally empty.
 */
export async function createOrchestratorForWorkflow(
  orgId: string,
  workflow: WorkflowDetails,
  options: CreateOrchestratorOptions = {},
): Promise<AgentCreateResponse> {
  const { onProgress } = options;

  // Step 1: ensure the workflow is published to A2A.
  onProgress?.('publishing_workflow');
  await _ensurePublishToApi(workflow, orgId);

  // Step 2: auto-generate a description (best-effort).
  onProgress?.('generating_description');
  let description = _fallbackDescription(workflow.name);
  try {
    const generated = await autoGenerateAgentDescription(orgId, {
      name: _orchestratorName(workflow.name),
      system_prompt: ORCHESTRATOR_SYSTEM_PROMPT,
      model_name: 'claude-opus-5',
      workflow_allowlist: [workflow.workflow_id],
      workflow_names: [workflow.name],
      enable_workflow_runtime: true,
      enable_workflow_authoring: false,
      enable_a2a_outbound: true,
    });
    if (generated && generated.trim()) {
      description = generated.trim();
    }
  } catch (err) {
    // Description generation is decorative; log and continue with the
    // fallback so a flaky LLM call never blocks orchestrator creation.
    console.warn(
      'auto_generate_description failed; falling back to static text:',
      err,
    );
  }

  // Step 3: create the agent.
  onProgress?.('creating_agent');
  const payload = buildOrchestratorAgentRequest(workflow, description);
  const response = await createAgent(orgId, payload);
  if (!response || !response.agent) {
    throw new Error('Agent creation returned no agent record');
  }

  onProgress?.('done');
  return response;
}
